import { PageWidgetType, type Resource } from '@efdigital/ll-addon-data';
import { DataTypeContent } from '@efdigital/ll-addon-data/data-types';
import {
  type PageWidgetResource,
  type PageWidgetResourceLike,
} from '@efdigital/ll-addon-data/types/content/objects/page-widget-resource';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import { DragToSort } from '@efdigital/ll-admin-v2/components/drag-to-sort.gts';
import { addOne } from '@efdigital/ll-admin-v2/helpers/add-one.ts';
import {
  type ReactiveStringArray,
  StringArrayResource,
} from '@efdigital/ll-admin-v2/resources/string-array.ts';
import { uniqueId } from '@ember/helper';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

import ResourcePicker from '../../pickers/resource-picker.gts';
import type {
  PageWidgetComponentSignature,
  WidgetEditor,
} from '../page-widget-signature.ts';

type ResourceItemDisplayType = PageWidgetResource['itemDisplay'];
type ResourceListDisplayType = PageWidgetResource['listDisplay'];

const RESOURCE_ITEM_DISPLAY_TYPES: Record<ResourceItemDisplayType, string> = {
  'link-simple': 'Link (text only)',
  'link-abstract': 'Link (with icon, abstract)',
  embed: 'Embed',
} as const;

const RESOURCE_ITEM_DISPLAY_TYPES_ARRAY = Object.entries(
  RESOURCE_ITEM_DISPLAY_TYPES
).map(([key, value]) => ({
  key,
  value,
}));

const RESOURCE_LIST_DISPLAY_TYPES: Record<ResourceListDisplayType, string> = {
  list: 'List',
} as const;

const RESOURCE_LIST_DISPLAY_TYPES_ARRAY = Object.entries(
  RESOURCE_LIST_DISPLAY_TYPES
).map(([key, value]) => ({
  key,
  value,
}));

export class PageWidgetResourceComponent extends Component<PageWidgetComponentSignature> {
  @service declare readonly store: Store;

  get widget(): PageWidgetResource {
    return this.args.widget as PageWidgetResource;
  }

  setShowItemTitles = (event: Event) => {
    const showTitle = (event.target as HTMLInputElement).checked;
    this.widget.showItemTitles = showTitle;
  };

  setItemDisplay = (event: Event) => {
    const itemDisplay = (event.target as HTMLSelectElement)
      .value as PageWidgetResource['itemDisplay'];
    this.widget.itemDisplay = itemDisplay;
  };

  setListDisplay = (event: Event) => {
    const listDisplay = (event.target as HTMLSelectElement)
      .value as PageWidgetResource['listDisplay'];
    this.widget.listDisplay = listDisplay;
  };

  updateResourceIds = (newValues: string[]) => {
    this.widget.resourceIds = newValues;
  };

  hasMultipleResources = (resourceIds: string[]) => {
    return resourceIds.length > 1;
  };

  updateResourceAtIndex = (
    resourceIds: ReactiveStringArray,
    index: number,
    resource: Resource
  ) => {
    resourceIds.setValueAt(index, resource.id!);
  };

  resourceSelectedAtIndex = (
    resourceIds: ReactiveStringArray,
    index: number,
    resource: Resource
  ) => {
    resourceIds.setValueAt(index, resource.id!);
    return this.args.pageManager.addResourceToPlaylist(resource);
  };

  lookupResource = (resourceId: string) => {
    return this.args.pageManager.findResourceInPlaylist(resourceId);
  };

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      {{#let
        (StringArrayResource this.widget.resourceIds this.updateResourceIds)
        as |resourceIds|
      }}
        <div class="card-body">
          <div class="form-group mb-4">
            <label>Resources</label>
            <div class="list-group mb-3 gap-1">
              <DragToSort
                @items={{resourceIds.values}}
                @updateItems={{this.updateResourceIds}}
              >
                <:item as |dropZone draggable resourceId index|>
                  <div class="input-group" {{dropZone index}}>
                    <span
                      class="input-group-text justify-content-center fw-bold drag-handle w-3rem"
                      {{draggable resourceId index}}
                      title="Drag to reorder"
                    >
                      {{addOne index}}
                    </span>
                    <ResourcePicker
                      @resourceId={{resourceId}}
                      @onSelect={{fn
                        this.resourceSelectedAtIndex
                        resourceIds
                        index
                      }}
                    >
                      <:input as |showModal|>
                        <button
                          aria-label="Select resource"
                          class="btn btn-outline-primary rounded-0 fs-4 px-3 py-0"
                          type="button"
                          {{on "click" showModal}}
                        >
                          <iconify-icon inline icon="ph:magnifying-glass" />
                          <span class="visually-hidden">Select resource...</span>
                        </button>
                        <Await @promise={{this.lookupResource resourceId}}>
                          <:success as |resource|>
                            <input
                              type="text"
                              readonly
                              value={{if
                                resource
                                resource.title
                                "Please select a resource"
                              }}
                              class="form-control list-group-item text-truncate"
                              aria-label="Resource name"
                            />
                          </:success>
                        </Await>
                      </:input>
                    </ResourcePicker>
                    <button
                      aria-label="Remove resource"
                      class="btn btn-outline-primary rounded-end-1"
                      type="button"
                      {{on "click" (fn resourceIds.removeValueAt index)}}
                    ><iconify-icon inline icon="ph:x-bold" /></button>
                  </div>
                </:item>
              </DragToSort>
            </div>
            <button
              type="button"
              class="btn btn-primary btn-sm"
              {{on "click" (fn resourceIds.addValue "")}}
            >
              <iconify-icon inline icon="ph:plus-bold" />
              Add Resource
            </button>
          </div>

          <div class="form-group mb-4">
            <label for="{{uniqueId}}-itemDisplay">Display each resource as</label>
            <select
              class="form-control"
              id="{{uniqueId}}-itemDisplay"
              {{selectValue this.widget.itemDisplay}}
              {{on "change" this.setItemDisplay}}
            >
              {{#each RESOURCE_ITEM_DISPLAY_TYPES_ARRAY as |itemDisplay|}}
                <option value={{itemDisplay.key}}>{{itemDisplay.value}}</option>
              {{/each}}
            </select>
          </div>

          {{#if (this.hasMultipleResources resourceIds.values)}}
            <div class="form-group mb-4">
              <label for="{{uniqueId}}-listDisplay">Group multiple resources
                into</label>
              <select
                class="form-control"
                id="{{uniqueId}}-listDisplay"
                {{selectValue this.widget.listDisplay}}
                {{on "change" this.setListDisplay}}
              >
                {{#each RESOURCE_LIST_DISPLAY_TYPES_ARRAY as |listDisplay|}}
                  <option
                    value={{listDisplay.key}}
                  >{{listDisplay.value}}</option>
                {{/each}}
              </select>
            </div>
          {{/if}}
        </div>
      {{/let}}
    {{/let}}
  </template>
}

const DEFAULT_WIDGET_RESOURCE: Omit<PageWidgetResourceLike, 'wid'> = {
  widgetType: PageWidgetType.Resource,
  title: 'Resource',
  css: '',
  resourceIds: [''],
  itemDisplay: 'embed',
  listDisplay: 'list',
  showItemTitles: false,
  linkCss: '',
};

export const ResourceWidgetEditor: WidgetEditor = {
  icon: 'ph:chalkboard-teacher-bold',
  label: 'Resource',
  component: PageWidgetResourceComponent,
  dataType: DataTypeContent.PageWidgetResource,
  widgetData: DEFAULT_WIDGET_RESOURCE,
};
